import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, euro, dataIt, STATI } from "@/lib/api";
import { PrintHeader, PrintFooter } from "@/components/PrintHeader";
import { FirmaCliente } from "@/components/FirmaCliente";
import { CondividiButton } from "@/components/CondividiButton";

const Th = ({ children, right }) => <th className={`border-b-2 border-slate-900 py-1.5 text-[10px] font-bold uppercase tracking-wider ${right ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, right, mono }) => <td className={`border-b border-slate-200 py-1.5 ${right ? "text-right" : ""} ${mono ? "font-mono" : ""}`}>{children}</td>;
const Box = ({ title, children }) => (
  <div className="rounded border border-slate-300 p-3"><p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{title}</p>{children}</div>
);

export default function SchedaStampa({ pubblico }) {
  const params = useParams();
  const id = pubblico?.doc.id || params.id;
  const navigate = useNavigate();
  const [s, setS] = useState(pubblico?.doc || null);
  const [neg, setNeg] = useState(pubblico?.neg || {});

  const load = () => api.get(`/schede/${id}`).then((r) => setS(r.data));
  useEffect(() => {
    if (pubblico) return;
    load();
    api.get("/impostazioni").then((r) => setNeg(r.data));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!s) return <div className="py-20 text-center text-sm text-slate-500">Caricamento...</div>;
  const c = s.cliente || {}, b = s.bici || {};

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="mx-auto mb-4 flex max-w-[210mm] flex-wrap justify-between gap-2 px-4 print:hidden">
        {!pubblico && <Button variant="outline" onClick={() => navigate(`/schede/${id}`)} data-testid="print-back-button"><ArrowLeft className="mr-1 h-4 w-4" /> Torna alla scheda</Button>}
        <div className="ml-auto flex gap-2">
          {!pubblico && <CondividiButton tipo="scheda" id={id} telefono={c.telefono} nome={c.nome} numero={`#${s.numero}`} firmato={!!s.firma_cliente} negozio={neg.nome_negozio} />}
          <Button onClick={() => window.print()} className="bg-orange-600 hover:bg-orange-700" data-testid="print-now-button"><Printer className="mr-1 h-4 w-4" /> {pubblico ? "Stampa / Salva PDF" : "Stampa"}</Button>
        </div>
      </div>
      <div className="print-sheet mx-auto max-w-[210mm] bg-white p-10 text-[12px] text-slate-900 shadow-md print:max-w-none print:p-0 print:shadow-none" data-testid="print-sheet">
        <PrintHeader neg={neg} titolo={neg.titolo_scheda || "Scheda di lavoro"}>
          <p className="font-mono text-2xl font-bold" data-testid="print-numero">#{s.numero}</p>
          <p>Stato: <b>{STATI.find((x) => x.value === s.stato)?.label}</b></p>
        </PrintHeader>
        <div className="mt-4 grid grid-cols-3 gap-3">
          <Box title="Cliente"><p className="font-bold">{c.nome} {c.cognome}</p><p>{c.telefono}</p><p>{c.email}</p><p>{c.indirizzo}</p></Box>
          <Box title="Bici"><p className="font-bold">{b.marca} {b.modello}</p><p>{[b.tipo, b.colore, b.taglia].filter(Boolean).join(" · ")}</p><p className="font-mono">Telaio: {b.telaio || "—"}</p></Box>
          <Box title="Date"><p>Ingresso: <b>{dataIt(s.data_ingresso)}</b></p><p>Consegna prevista: <b>{dataIt(s.data_consegna_prevista)}</b></p></Box>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Box title="Problema riscontrato"><p className="whitespace-pre-wrap">{s.problema || "—"}</p></Box>
          <Box title="Lavori eseguiti"><p className="whitespace-pre-wrap">{s.lavori_eseguiti || "—"}</p></Box>
        </div>
        {s.ricambi.length > 0 && (
          <table className="mt-5 w-full">
            <thead><tr><Th>Ricambio</Th><Th>Codice</Th><Th right>Qtà</Th><Th right>Prezzo</Th><Th right>Totale</Th></tr></thead>
            <tbody>{s.ricambi.map((r, i) => <tr key={i}><Td>{r.nome}</Td><Td mono>{r.codice}</Td><Td right>{r.quantita}</Td><Td right mono>{euro(r.prezzo)}</Td><Td right mono>{euro(r.quantita * r.prezzo)}</Td></tr>)}</tbody>
          </table>
        )}
        {s.manodopera.length > 0 && (
          <table className="mt-5 w-full">
            <thead><tr><Th>Manodopera</Th><Th right>Ore</Th><Th right>€/ora</Th><Th right>Totale</Th></tr></thead>
            <tbody>{s.manodopera.map((m, i) => <tr key={i}><Td>{m.descrizione}</Td><Td right>{m.fisso ? "a corpo" : m.ore}</Td><Td right mono>{euro(m.tariffa)}</Td><Td right mono>{euro(m.ore * m.tariffa)}</Td></tr>)}</tbody>
          </table>
        )}
        <div className="ml-auto mt-5 w-64 text-[13px]">
          <div className="flex justify-between py-1"><span>Totale ricambi</span><span className="font-mono">{euro(s.totale_ricambi)}</span></div>
          <div className="flex justify-between py-1"><span>Totale manodopera</span><span className="font-mono">{euro(s.totale_manodopera)}</span></div>
          <div className="flex justify-between border-t-2 border-slate-900 py-2 text-base font-bold"><span>TOTALE (IVA incl.)</span><span className="font-mono" data-testid="print-totale">{euro(s.totale)}</span></div>
        </div>
        <p className="mt-6 text-[10px] text-slate-500" data-testid="print-privacy-note">Il cliente dichiara di aver ricevuto l'informativa sul trattamento dei dati personali ai sensi del Reg. UE 2016/679 (GDPR) e autorizza il trattamento dei dati per l'esecuzione del servizio.</p>
        <div className="mt-6 grid grid-cols-2 items-end gap-16 text-[11px] text-slate-500">
          <div className="border-t border-slate-400 pt-1">Firma meccanico</div>
          <FirmaCliente label="Firma cliente per accettazione" endpoint={`/schede/${id}/firma`} doc={s} onSaved={load} prefix="scheda" readOnly={!!pubblico} />
        </div>
        <PrintFooter text={neg.piede_scheda} testId="print-piede" />
      </div>
    </div>
  );
}
